import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function LoadPatternModal({ open, onOpenChange, onSelectPattern, token }) {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [patterns, setPatterns] = useState([]);
  const [filteredPatterns, setFilteredPatterns] = useState([]);
  const [uniqueClients, setUniqueClients] = useState([]);

  // Fetch all patterns
  useEffect(() => {
    if (open) {
      fetchPatterns();
    }
  }, [open]);

  // Extract unique clients for filter
  useEffect(() => {
    const clientMap = new Map();
    patterns.forEach((pattern) => {
      const clientNumber = pattern?.client_number || '';
      const clientName = pattern?.client_name || '';
      const key = `${clientNumber}|${clientName}`;
      if (!clientMap.has(key)) {
        clientMap.set(key, {
          client_number: clientNumber,
          client_name: clientName,
        });
      }
    });

    setUniqueClients(
      Array.from(clientMap.values()).sort((a, b) => a.client_number.localeCompare(b.client_number)),
    );
  }, [patterns]);

  // Filter patterns based on search and client filter
  useEffect(() => {
    let filtered = patterns;
    
    // Apply client filter
    if (clientFilter !== 'all') {
      filtered = filtered.filter(p => p.client_number === clientFilter);
    }
    
    // Apply search query
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.pattern_number.toLowerCase().includes(query) ||
        p.client_number.toLowerCase().includes(query) ||
        p.client_name.toLowerCase().includes(query)
      );
    }
    
    setFilteredPatterns(filtered);
  }, [searchQuery, clientFilter, patterns]);

  const fetchPatterns = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/patterns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      // Sort by pattern_number descending
      const sorted = data.sort((a, b) => b.pattern_number.localeCompare(a.pattern_number));
      setPatterns(sorted);
      setFilteredPatterns(sorted);
    } catch (err) {
      console.error('Error fetching patterns:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Load Pattern</DialogTitle>
          <DialogDescription>
            Search by client name, client number, or pattern number
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Client Filter */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Filter by Client</label>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {uniqueClients.map((client) => (
                  <SelectItem key={client.client_number} value={client.client_number}>
                    #{client.client_number} - {client.client_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by client name, number, or pattern number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          
          {/* Results */}
          <div className="border rounded-lg">
            <ScrollArea className="h-[400px]">
              {loading ? (
                <div className="p-8 text-center text-muted-foreground">
                  Loading patterns...
                </div>
              ) : filteredPatterns.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  No patterns found
                </div>
              ) : (
                <div className="divide-y">
                  {filteredPatterns.map((pattern) => (
                    <div
                      key={pattern.id}
                      className="p-4 hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => onSelectPattern(pattern)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-semibold font-mono tabular-nums text-sm">
                            {pattern.pattern_number}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {pattern.client_name} (#{pattern.client_number})
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatDate(pattern.created_at)}
                        </div>
                      </div>
                      {pattern.notes && (
                        <div className="text-xs text-muted-foreground mt-2 line-clamp-1">
                          {pattern.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>
        
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
